/**
 * Chat transport abstraction (ChatView ↔ AgentCore Runtime).
 *
 * The view depends only on the narrow {@link ChatTransport} interface so it
 * works against a mock today and the real AgentCore Runtime endpoint when
 * `VITE_CHAT_USE_REAL=true`.
 *
 * **Transport switch:** the module exports a single `chatTransport` instance
 * chosen at build time by the `VITE_CHAT_USE_REAL` env var:
 *  - `'true'` → {@link createRealChatTransport} (POSTs to the Runtime).
 *  - anything else → {@link createMockChatTransport} (canned replies).
 * This is the safety net: if the live backend isn't ready at demo time, set
 * `VITE_CHAT_USE_REAL=false` and the UX demo still runs.
 *
 * **Real transport:** POSTs the conversation history to
 * `${VITE_CHAT_RUNTIME_URL}/invoke` and parses the assistant reply. Attaches
 * the Cognito idToken as `Authorization: Bearer` when available (same pattern
 * as `api/client.ts`). If the demo Runtime is configured open/simplified and
 * no token is present, the request is sent without an auth header.
 *
 * **Deferred extension — token streaming:** the interface returns a single
 * `Promise<string>` today. A streaming variant would change the return type to
 * `AsyncIterable<string>` (or accept a `(chunk: string) => void` callback).
 * The view's message component already renders assistant text as a growing
 * string, so the swap is additive.
 *
 * **Deferred extension — write-confirmation flow:** a future phase may return
 * structured messages (e.g. `{ type: 'action-confirmation', payload: … }`)
 * instead of plain text. The view leaves a clear extension point for rendering
 * an action-confirmation card from such a message.
 */

import { getAccessToken } from '../auth/tokenStore.js';

/** A single turn in the conversation. */
export interface ChatTurn {
  readonly role: 'user' | 'assistant';
  readonly content: string;
}

/**
 * Narrow transport interface: send the conversation history and resolve the
 * assistant's reply.
 */
export interface ChatTransport {
  /** Send the conversation so far; resolve the assistant reply. */
  send(history: readonly ChatTurn[], signal?: AbortSignal): Promise<string>;
  // Later: an async-iterable / callback variant for token streaming.
}

/* -------------------------------------------------------------------------- */
/* Runtime URL + transport switch                                             */
/* -------------------------------------------------------------------------- */

/**
 * The AgentCore Runtime base URL, injected at build time. Placeholder until the
 * `feat/agentcore-infra` stack provides the real endpoint.
 */
export const CHAT_RUNTIME_URL: string =
  (import.meta.env.VITE_CHAT_RUNTIME_URL as string | undefined) ??
  'https://placeholder.agentcore.example.com';

/**
 * When `'true'`, the real transport is used; otherwise the mock. Read once at
 * module load so the transport instance is stable for the app's lifetime.
 */
const USE_REAL =
  (import.meta.env.VITE_CHAT_USE_REAL as string | undefined) === 'true';

/* -------------------------------------------------------------------------- */
/* Real transport                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Create a {@link ChatTransport} that POSTs to the live AgentCore Runtime.
 *
 * - Sends the conversation history as a JSON body to `{CHAT_RUNTIME_URL}/invoke`.
 * - Attaches the Cognito idToken from the in-memory token store as
 *   `Authorization: Bearer` when available (same auth path as `api/client.ts`).
 *   If no token is present (e.g. the demo Runtime is open), the request is sent
 *   without an auth header.
 * - Maps transport/HTTP failures to thrown `Error` instances that the ChatView's
 *   existing error+retry UI handles unchanged.
 */
export function createRealChatTransport(): ChatTransport {
  const baseUrl = CHAT_RUNTIME_URL.replace(/\/+$/, '');

  return {
    async send(
      history: readonly ChatTurn[],
      signal?: AbortSignal,
    ): Promise<string> {
      if (signal?.aborted === true) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      // Attach the Cognito idToken (required under Option C — the agent
      // forwards this token to each tool call, and the af-south-1 APIs 401
      // without it). Same pattern as api/client.ts.
      const token = getAccessToken();
      if (token !== undefined) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const body = JSON.stringify({
        messages: history.map((turn) => ({
          role: turn.role,
          content: turn.content,
        })),
      });

      let response: Response;
      try {
        response = await fetch(baseUrl, {
          method: 'POST',
          headers,
          body,
          signal,
        });
      } catch (caught) {
        // AbortError propagates as-is so the view ignores unmount aborts.
        if (
          caught instanceof DOMException &&
          caught.name === 'AbortError'
        ) {
          throw caught;
        }
        throw new Error(
          'Unable to reach the assistant. Check your connection and try again.',
        );
      }

      if (!response.ok) {
        // Try to extract a message from the error body.
        let detail = '';
        try {
          const errorBody = (await response.json()) as {
            message?: string;
            error?: { message?: string };
          };
          detail =
            errorBody.error?.message ?? errorBody.message ?? '';
        } catch {
          // Non-JSON error body — ignore.
        }
        throw new Error(
          detail.length > 0
            ? detail
            : `The assistant returned an error (${String(response.status)}). Please try again.`,
        );
      }

      // Parse the assistant reply. The Runtime is expected to return JSON with
      // the assistant's text. Support common response shapes:
      //   { "reply": "..." }  / { "response": "..." }  <- the ESB agent + stub
      //   { "output": { "text": "..." } }
      //   { "content": "..." }
      //   { "message": { "content": "..." } }
      //   { "text": "..." }
      //   or plain text
      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        // Plain-text response — return it directly.
        const text = await response.text();
        if (text.trim().length === 0) {
          throw new Error('The assistant returned an empty response.');
        }
        return text.trim();
      }

      const parsed = (await response.json()) as Record<string, unknown>;

      // The ESB chat agent (packages/chat-agent) and the deploy stub
      // (packages/agent-stub) both return { "reply": string, "response": string }.
      // Check these first so the live Runtime response parses correctly.
      for (const key of ['reply', 'response'] as const) {
        const value = parsed[key];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }

      // Try other known response shapes.
      const output = parsed['output'] as
        | { text?: string; content?: string }
        | undefined;
      if (typeof output === 'object' && output !== null) {
        const text = output.text ?? output.content;
        if (typeof text === 'string' && text.trim().length > 0) {
          return text.trim();
        }
      }
      if (
        typeof parsed['content'] === 'string' &&
        (parsed['content'] as string).trim().length > 0
      ) {
        return (parsed['content'] as string).trim();
      }
      const msg = parsed['message'] as
        | { content?: string; text?: string }
        | string
        | undefined;
      if (typeof msg === 'string' && msg.trim().length > 0) {
        return msg.trim();
      }
      if (typeof msg === 'object' && msg !== null) {
        const text = msg.content ?? msg.text;
        if (typeof text === 'string' && text.trim().length > 0) {
          return text.trim();
        }
      }
      if (
        typeof parsed['text'] === 'string' &&
        (parsed['text'] as string).trim().length > 0
      ) {
        return (parsed['text'] as string).trim();
      }

      throw new Error('The assistant returned an unrecognised response format.');
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Mock transport                                                             */
/* -------------------------------------------------------------------------- */

/** Canned replies the mock cycles through to exercise the UI. */
const CANNED_REPLIES: readonly string[] = [
  'Your current cheque account balance is R12,450.00. You have 3 recent transactions totalling R1,230.50 in debits.',
  'Your top spending category this month is Groceries at R3,200.00, followed by Transport at R1,150.00.',
  "You have 4 saved beneficiaries. Would you like me to list them?",
  "I can help with balance inquiries, spending insights, beneficiary lookups, and transaction history. What would you like to know?",
];

/**
 * Options for the mock transport.
 *
 * - `delayMs` — simulated network latency (default 800ms).
 * - `failNext` — when `true` the next `send` call rejects, then resets. Useful
 *   for exercising the error state in tests and during development.
 */
export interface MockTransportOptions {
  delayMs?: number;
  failNext?: boolean;
}

/**
 * Create a mock {@link ChatTransport} that returns canned replies after a
 * simulated delay. The `opts` object is live — mutate `opts.failNext = true`
 * from a dev-tools console or a test to trigger the error path on the next
 * send.
 */
export function createMockChatTransport(
  opts: MockTransportOptions = {},
): ChatTransport {
  const delay = opts.delayMs ?? 800;
  let index = 0;

  return {
    async send(
      _history: readonly ChatTurn[],
      signal?: AbortSignal,
    ): Promise<string> {
      // Honour AbortSignal (the view aborts on unmount).
      if (signal?.aborted === true) {
        throw new DOMException('Aborted', 'AbortError');
      }

      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, delay);
        signal?.addEventListener('abort', () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        }, { once: true });
      });

      // Simulate a transient failure when requested.
      if (opts.failNext === true) {
        opts.failNext = false;
        throw new Error('Something went wrong. Please try again.');
      }

      const reply = CANNED_REPLIES[index % CANNED_REPLIES.length]!;
      index += 1;
      return reply;
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Module-level transport instance (the single switch)                        */
/* -------------------------------------------------------------------------- */

/**
 * The transport instance the ChatView uses. Chosen at build time by
 * `VITE_CHAT_USE_REAL`: `'true'` → real AgentCore Runtime; anything else → mock.
 */
export const chatTransport: ChatTransport = USE_REAL
  ? createRealChatTransport()
  : createMockChatTransport();
